import submitCharge from './utils/submitCharge'
import submitRecurringCharge from './utils/submitRecurringCharge'
import submitFinalizeInvoice from './utils/submitFinalizeInvoice'
import { usePaymentStore } from '../PaymentTypeSelection'
import AobInvoice from '../../../types/aobInvoice'
import { reportToSentry } from 'utils/reportToSentry'
import { useState } from 'react'
import { getSnlPatientHash } from '../../../stores/snlPatientHashStore'
import { useTokenStore } from 'stores'
import { useSnlInvoiceStore } from 'stores/SNLInvoiceStore'
import { createPaymentProfile } from './utils/createPaymentProfile'
import { useAddressStore } from 'components/addressBlock'
import { splitMoney } from '../../../utils/money'

export default function useSubmitPayment(
  invoice?: AobInvoice,
  onSuccess?: () => void,
  skipFinalizeInvoice = false
) {
  const [ error, setError ] = useState<string | null>( null )
  const [ isLoading, setIsLoading ] = useState( false )

  const patientHash = getSnlPatientHash()
  const { customerToken } = useTokenStore()
  const { snlInvoiceHash } = useSnlInvoiceStore()

  const {
    selectedPaymentProfile,
    newCardDetails,
    newBankDetails,
    splitPayment,
    savePaymentProfile,
    billingAddress,
    billingAddressSameAsShipping
  } = usePaymentStore()
  const { address: shippingAddress } = useAddressStore()

  const address = billingAddressSameAsShipping ? shippingAddress : billingAddress

  const getPaymentMethodDetails = () => {
    if ( selectedPaymentProfile === `New Credit Card` ) {
      return {
        newCard: newCardDetails
      }
    } else if ( selectedPaymentProfile === `New Bank Account` ) {
      return {
        newBankAccount: newBankDetails
      }
    } else
      return {
        paymentProfileId: selectedPaymentProfile
      }
  }

  const getOrderDetails = () => {
    return {
      name: `Supply Event Payment`,
      orderDescription: `Supply Event Payment`
    }
  }

  const finalizeInvoice = async () => {
    if ( !invoice || skipFinalizeInvoice || !snlInvoiceHash ) return

    try {
      await submitFinalizeInvoice( snlInvoiceHash )
    } catch ( e ) {
      reportToSentry( new Error( `Error submitting finalize invoice` ) )
    }
  }

  const submitUpfrontPayment = async ( amount: number ) => {
    // check if a recurring payment or a single payment
    setIsLoading( true )
    const response = splitPayment ? await submitRecurringCharge({
      ...( invoice?.invoice_number && {
        invoiceNumber: invoice.invoice_number
      }),
      ...( patientHash && {
        patientHash
      }),
      ...getOrderDetails(),
      ...( customerToken && {
        customerToken
      }),
      amount: splitMoney( amount, 3 ),
      ...getPaymentMethodDetails(),
      startDate: new Date().toISOString()
        .slice( 0, 10 ),
      intervalLength: `1`,
      intervalUnit: `months`,
      totalOccurrences: `3`,
      initialPayment: true
    }) : await submitCharge({
      ...( invoice?.invoice_number && {
        invoiceNumber: invoice.invoice_number
      }),
      ...( patientHash && {
        patientHash
      }),
      ...( customerToken && {
        customerToken
      }),
      amount,
      ...getOrderDetails(),
      ...getPaymentMethodDetails()
    })


    if ( response?.status === `success` ) {
      setError( null )
      if ( savePaymentProfile && !splitPayment ) {
        const paymentMethod = getPaymentMethodDetails()
        await createPaymentProfile({
          ...( selectedPaymentProfile === `New Credit Card` ? paymentMethod?.newCard : {
            accountType: paymentMethod?.newBankAccount?.accountType?.toLowerCase(),
            routingNumber: paymentMethod?.newBankAccount?.routingNumber,
            accountNumber: paymentMethod?.newBankAccount?.accountNumber,
            nameOnAccount: paymentMethod?.newBankAccount?.nameOnAccount
          }),
          ...( selectedPaymentProfile === `New Credit Card` ? {
            street: address.street,
            city: address.city,
            state: address.state,
            zip: address.zipCode
          } : {})
        }, snlInvoiceHash ).catch( () => null )
      }
      // finalize invoice if not skipped
      await finalizeInvoice()

      setIsLoading( false )

      onSuccess?.()
      return response
    } else {
      setIsLoading( false )
      setError(
        response?.message ?? `Error submitting upfront charge. Please try again`
      )
    }
  }

  return {
    error,
    setError,
    isLoading,
    submitUpfrontPayment
  }
}
